import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import { queryClient } from '@/utils/queryClient'
import {
  activateUsers,
  approveUser,
  deactivateUsers,
  deleteUsers,
  getMasterShippers,
  getMasterUsers,
  getMasterUserTabCounts,
  rejectUser,
  resendInvites,
  resetPasswords,
  resetMfa,
  getMasterDepartments,
  getMasterAuthCodes,
  getMasterShipperOptions,
  updateAuthCode,
  updateShipperGroup,
  updateShipperMultiGroup,
  updateUserProfile,
  setMasterShipperUseFlag,
  addMasterDepartment,
} from './apis'
import {
  ApproveUserReq,
  GetMasterShippersReq,
  GetMasterShippersRes,
  GetMasterUsersReq,
  GetMasterUsersRes,
  GetMasterUserTabCountsReq,
  GetMasterUserTabCountsRes,
  RejectUserReq,
  UpdateAuthCodeReq,
  UpdateShipperGroupReq,
  UpdateShipperMultiGroupReq,
  UpdateUserProfileReq,
} from './types'

const PATH = ['master', 'userManagement']
export const GET_MASTER_SHIPPERS = [...PATH, 'GET_MASTER_SHIPPERS']
export const GET_MASTER_DEPARTMENTS = [...PATH, 'GET_MASTER_DEPARTMENTS']
export const GET_MASTER_AUTH_CODES = [...PATH, 'GET_MASTER_AUTH_CODES']
export const GET_MASTER_USERS = [...PATH, 'GET_MASTER_USERS']
export const GET_MASTER_USER_TAB_COUNTS = [...PATH, 'GET_MASTER_USER_TAB_COUNTS']

export const invalidateMasterUsers = () => {
  queryClient.invalidateQueries({ queryKey: GET_MASTER_USERS })
  queryClient.invalidateQueries({ queryKey: GET_MASTER_USER_TAB_COUNTS })
}

// 계정 삭제 시에는 상단 화주 그리드도 무효화한다.
// 이메일(Okta ID) 검색 결과(상단)는 해당 유저가 존재해야 화주가 매핑되므로, 삭제 후 재검색 시
// 캐시된 화주가 남지 않도록 함께 갱신한다.
export const invalidateMasterUsersAndShippers = () => {
  invalidateMasterUsers()
  queryClient.invalidateQueries({ queryKey: GET_MASTER_SHIPPERS })
}

// enabled: 검색(검색 버튼) 전에는 화주 목록을 조회하지 않는다 (진입 시 빈 화면)
export const useGetMasterShippers = (req: GetMasterShippersReq, enabled = true): UseQueryResult<GetMasterShippersRes, Error> => {
  return useQuery({
    queryKey: [...GET_MASTER_SHIPPERS, req],
    queryFn: () => getMasterShippers(req),
    enabled,
  })
}

// 화주 드롭다운 옵션 — 화주사관리 등록 화주 전체 {value: 화주코드, label: 화주명}
export const useGetMasterShipperOptions = (): UseQueryResult<{ value: string; label: string }[], Error> => {
  return useQuery({
    queryKey: [...PATH, 'GET_MASTER_SHIPPER_OPTIONS'],
    queryFn: () => getMasterShipperOptions(),
    staleTime: 5 * 60 * 1000,
  })
}

export const useGetMasterDepartments = (shipperCode?: string): UseQueryResult<string[], Error> => {
  return useQuery({
    queryKey: [...GET_MASTER_DEPARTMENTS, shipperCode],
    queryFn: () => getMasterDepartments(shipperCode ?? ''),
    enabled: !!shipperCode,
  })
}

// 선택 화주의 권한 목록 — 권한 드롭다운 옵션 (LOGISPRD-9313)
export const useGetMasterAuthCodes = (shipperCode?: string): UseQueryResult<{ authCode: string; authName: string }[], Error> => {
  return useQuery({
    queryKey: [...GET_MASTER_AUTH_CODES, shipperCode],
    queryFn: () => getMasterAuthCodes(shipperCode ?? ''),
    enabled: !!shipperCode,
    staleTime: 5 * 60 * 1000,
  })
}

// enabled: 화주 선택(더블클릭) 전에는 유저 목록을 조회하지 않는다 (PRD — 화주 선택 시 표시)
export const useGetMasterUsers = (req: GetMasterUsersReq, enabled = true): UseQueryResult<GetMasterUsersRes, Error> => {
  return useQuery({
    queryKey: [...GET_MASTER_USERS, req],
    queryFn: () => getMasterUsers(req),
    enabled,
  })
}

export const useGetMasterUserTabCounts = (req: GetMasterUserTabCountsReq, enabled = true): UseQueryResult<GetMasterUserTabCountsRes, Error> => {
  return useQuery({
    queryKey: [...GET_MASTER_USER_TAB_COUNTS, req],
    queryFn: () => getMasterUserTabCounts(req),
    enabled,
    // 카운트는 보조 정보 — 실패 시 전역 에러 알럿 없이 0 표시로 처리
    meta: { noErrorMessage: true },
  })
}

export const useApproveUser = () => {
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ApproveUserReq }) => approveUser(id, body),
    onSuccess: invalidateMasterUsers,
  })
}

export const useRejectUsers = () => {
  return useMutation({
    mutationFn: ({ ids, body }: { ids: number[]; body: RejectUserReq }) => Promise.all(ids.map((id) => rejectUser(id, body))),
    onSuccess: invalidateMasterUsers,
  })
}

export const useResendInvites = () => {
  return useMutation({
    mutationFn: (ids: number[]) => resendInvites(ids),
    onSuccess: invalidateMasterUsers,
  })
}

export const useResetPasswords = () => {
  return useMutation({
    mutationFn: (ids: number[]) => resetPasswords(ids),
  })
}

export const useResetMfa = () => {
  return useMutation({
    mutationFn: (ids: number[]) => resetMfa(ids),
  })
}

export const useActivateUsers = () => {
  return useMutation({
    mutationFn: (ids: number[]) => activateUsers(ids),
    onSuccess: invalidateMasterUsers,
  })
}

export const useDeactivateUsers = () => {
  return useMutation({
    mutationFn: (ids: number[]) => deactivateUsers(ids),
    onSuccess: invalidateMasterUsers,
  })
}

export const useDeleteUsers = () => {
  return useMutation({
    mutationFn: (ids: number[]) => deleteUsers(ids),
    onSuccess: invalidateMasterUsersAndShippers,
  })
}

export const useUpdateAuthCode = () => {
  // 권한 변경은 ag-grid 셀에 즉시 반영되므로 전체 목록 invalidate(= 하단 그리드 refetch·초기화)를 하지 않는다
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateAuthCodeReq }) => updateAuthCode(id, body),
  })
}

export const useUpdateShipperGroup = () => {
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateShipperGroupReq }) => updateShipperGroup(id, body),
    onSuccess: invalidateMasterUsers,
  })
}

export const useUpdateUserProfile = () => {
  // 인라인 셀 수정은 ag-grid 에 즉시 반영되므로 전체 목록 invalidate(= 그리드 refetch·초기화)를 하지 않는다
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateUserProfileReq }) => updateUserProfile(id, body),
  })
}

export const useUpdateShipperMultiGroup = () => {
  return useMutation({
    mutationFn: ({ shipperCode, body }: { shipperCode: string; body: UpdateShipperMultiGroupReq }) => updateShipperMultiGroup(shipperCode, body),
  })
}

export const useSetMasterShipperUseFlag = () => {
  return useMutation({
    mutationFn: ({ shipperCode, use }: { shipperCode: string; use: boolean }) => setMasterShipperUseFlag(shipperCode, use),
  })
}

export const useAddMasterDepartment = () => {
  return useMutation({
    mutationFn: ({ shipperCode, departmentName, manager }: { shipperCode: string; departmentName: string; manager: string }) =>
      addMasterDepartment(shipperCode, departmentName, manager),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GET_MASTER_DEPARTMENTS }),
  })
}
